var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { sizeClassFromWidth } from './layout-context-state';
import { SIZE_CLASSES, DEFAULT_SIZE_CLASS } from './size-class';
import { BASELINE_GRID } from './baseline-grid';
var MM_PER_INCH = 25.4;
var COMMON_DPI = 160;
/**
 * used during SSR by useMediaQueries() to set initial value for the viewports SizeClass
 * @param deviceInformation
 * @returns SizeClass | null
 */
var getSizeClassFromDeviceInformation = function (deviceInformation) {
    var widthInPixels = deviceInformation.resolutionWidth, widthInMillimeters = deviceInformation.physicalScreenWidth;
    if (typeof widthInPixels === 'number') {
        return sizeClassFromWidth(widthInPixels);
    }
    else if (typeof widthInMillimeters === 'number') {
        return sizeClassFromWidth(Math.round((widthInMillimeters / MM_PER_INCH) * COMMON_DPI));
    }
    else if (deviceInformation.tablet === true) {
        return SIZE_CLASSES.MEDIUM;
    }
    else if (deviceInformation.mobile === true) {
        return SIZE_CLASSES.SMALL;
    }
    else {
        return null;
    }
};
/**
 * used by media query observers created by useMediaQueries() to report the viewports SizeClass
 * @param mediaQueryLists
 * @returns the SizeClass corresponding to the widest MediaQueryList match
 */
var getSizeClassByMediaQuery = function (mediaQueryLists) {
    var largestMediaQueryMatch;
    var sizeClass;
    if (typeof window !== 'undefined' && mediaQueryLists.length > 0) {
        var mediaQueryListMatches = mediaQueryLists.filter(function (mql) { return mql[1].matches; });
        mediaQueryListMatches.forEach(function (match) {
            if (largestMediaQueryMatch === undefined || match[0] > largestMediaQueryMatch[0]) {
                largestMediaQueryMatch = match;
            }
        });
        if (mediaQueryListMatches.length < 1) {
            largestMediaQueryMatch = undefined;
        }
    }
    if (largestMediaQueryMatch) {
        sizeClass = sizeClassFromWidth(largestMediaQueryMatch[0]);
    }
    else {
        // media queries will not match extra_small
        sizeClass = SIZE_CLASSES.EXTRA_SMALL;
    }
    return sizeClass;
};
/**
 * for use by LayoutContextProviders attached to the viewport
 * @param attachListeners attach media query listeners, for LayoutContexts keyed to the viewport
 * @param deviceInformation information needed for making a best guess at viewport dimensions during server-side render.
 * @returns the sizeClass corresponding to the viewport width, number of available baseline grid columns given the viewport width. If unable to calculate, default is LARGE.
 */
export var useMediaQueries = function (attachListeners, deviceInformation) {
    var _a;
    var mediaQueryLists = React.useMemo(function () {
        if (attachListeners && typeof window !== 'undefined' && window.matchMedia) {
            return Object.entries(SIZE_CLASSES).map(function (_a) {
                var _b = __read(_a, 2), _ = _b[0], sizeClass = _b[1];
                return [
                    sizeClass.value,
                    window.matchMedia("(min-width: ".concat(sizeClass.value, "px)")),
                ];
            });
        }
        return [];
    }, [attachListeners]);
    var _b = __read(React.useState((_a = (deviceInformation && getSizeClassFromDeviceInformation(deviceInformation))) !== null && _a !== void 0 ? _a : DEFAULT_SIZE_CLASS), 2), sizeClass = _b[0], setSizeClass = _b[1];
    var numBaselineColumns = React.useMemo(function () { return BASELINE_GRID[sizeClass.name].VIEWPORT; }, [sizeClass]);
    React.useEffect(function () {
        if (attachListeners) {
            var handler_1 = function () { return setSizeClass(getSizeClassByMediaQuery(mediaQueryLists)); };
            handler_1();
            if (mediaQueryLists && mediaQueryLists.length > 0) {
                mediaQueryLists.forEach(function (mql) { return mql[1].addEventListener('change', handler_1); });
            }
            return function () {
                if (mediaQueryLists && mediaQueryLists.length > 0) {
                    mediaQueryLists.forEach(function (mql) { return mql[1].removeEventListener('change', handler_1); });
                }
            };
        }
    }, []);
    return { sizeClass: sizeClass, numBaselineColumns: numBaselineColumns };
};
//# sourceMappingURL=use-media-queries.js.map